import { IDEState } from '@/types';
import { useMemo } from 'react';
import { useIDEState } from './use-ide-state';

/**
 * Custom hook that allows components to subscribe to specific slices of IDE state
 * to prevent unnecessary re-renders when unrelated state changes.
 */
export function useIDESelector<T>(selector: (state: IDEState) => T): T {
    const { state } = useIDEState();

    return useMemo(() => selector(state), [state, selector]);
}

// Pre-defined selectors for common use cases
export const useIDESelectors = {
    // File explorer specific selectors
    useProjects: () => useIDESelector(state => state.projects),
    useSelectedFile: () => useIDESelector(state => state.selectedFile),
    useSelectedProject: () => useIDESelector(state => state.selectedProject),
    useFileExplorer: () => useIDESelector(state => state.fileExplorer),
    useFileExplorerVisibility: () => useIDESelector(state => state.fileExplorer.isVisible),

    // Tab management selectors
    useTabs: () => useIDESelector(state => state.tabs),
    useActiveTab: () => useIDESelector(state => state.activeTabId),
    useActiveTabData: () =>
        useIDESelector(state => {
            const activeTab = state.tabs.find(tab => tab.id === state.activeTabId);
            if (!activeTab) return null;

            const project = state.projects.find(p => p.id === activeTab.projectId);
            const file = project?.files.find(f => f.id === activeTab.fileId);

            return { activeTab, project, file };
        }),

    // Output panel selectors
    useOutputPanel: () => useIDESelector(state => state.outputPanel),
    useOutputMessages: () => useIDESelector(state => state.outputPanel.messages),
    useOutputVisibility: () => useIDESelector(state => state.outputPanel.isVisible),

    // Recent files selector
    useRecentFiles: () => useIDESelector(state => state.recentFiles),

    // Modal selectors
    useModals: () => useIDESelector(state => state.modals),
    useIsAnyModalOpen: () => useIDESelector(state => state.modals.isAnyModalOpen),

    // Search selectors
    useSearch: () => useIDESelector(state => state.search),
    useSearchVisibility: () => useIDESelector(state => state.search.isOpen),
};
